/**
 * Pure parser / planner / applier for dashboard-config imports.
 *
 * Flow:
 *  1. `parseConfig(json)` — JSON → DashboardConfig; rejects unknown
 *     versions or malformed shapes with a friendly error. v1 (single
 *     mode) bundles are auto-migrated into the v2 shape with the
 *     legacy slice landing in `modes.normal` and an empty compare
 *     side.
 *  2. `planImport(config, currentColumns)` — diffs required columns
 *     across BOTH modes against the current dataset's columns.
 *     Columns present verbatim are auto-resolved; missing ones are
 *     listed with ranked candidates (never auto-applied — D-022).
 *  3. `applyImport(config, resolutions, targetDatasetId)` — rewrites
 *     widgets / overrides / value-groupings / pinned filters /
 *     layouts to the resolved column names per mode, dropping
 *     widgets whose required columns the user chose to skip.
 *
 * No side effects. Callers persist the result through the usual
 * stores + Dexie.
 */
import type {
  DashboardConfig,
  DashboardLayoutItem,
  DashboardModeSlice,
  LegacyDashboardConfigV1,
} from '@/types/dashboard-config';
import { CONFIG_VERSION, LEGACY_CONFIG_VERSION } from '@/types/dashboard-config';
import type { ColumnMeta, ColumnOverrides } from '@/types/dataset';
import type { ColumnTransform } from '@/types/query';
import type { WidgetConfig } from '@/types/widget';
import type { DashboardMode } from '@/persistence/config-db';
import { rankCandidates, type MatchCandidate } from './column-match';

/** Per-column decision the user (or the planner) made during review. */
export type ColumnResolution =
  | { kind: 'map'; from: string; to: string }
  | { kind: 'skip'; from: string };

/** Output of `planImport`. */
export type ImportPlan = {
  version: typeof CONFIG_VERSION;
  /** Columns present verbatim — no user input needed. */
  autoResolved: ColumnResolution[];
  /** Columns missing from the current dataset, awaiting user decision. */
  needsReview: Array<{
    source: ColumnMeta;
    candidates: MatchCandidate[];
  }>;
};

/** Per-mode slice produced by `applyImport`. */
export type AppliedModeSlice = {
  widgets: WidgetConfig[];
  pinnedFilters: string[];
  layouts: {
    kpi: DashboardLayoutItem[];
    chart: DashboardLayoutItem[];
  };
};

/** Output of `applyImport`. */
export type AppliedImport = {
  modes: {
    normal: AppliedModeSlice;
    compare: AppliedModeSlice;
  };
  columnOverrides: ColumnOverrides;
  valueGroupings: Record<string, ColumnTransform>;
  /** Compare-mode period selection (null when not present in the bundle). */
  compareConfig: DashboardConfig['compareConfig'];
  /** Widgets dropped because at least one required column was skipped. */
  droppedWidgets: Array<{ id: string; title: string; reason: string }>;
};

/**
 * Parse and validate JSON. Narrows to `DashboardConfig` on success;
 * throws `Error` with a user-friendly message on failure so the UI
 * can surface it verbatim. v1 inputs are auto-migrated into the v2
 * bundle shape so the rest of the pipeline only ever sees v2.
 */
export function parseConfig(json: string): DashboardConfig {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new Error('Dosya geçerli bir JSON değil.');
  }
  if (raw === null || typeof raw !== 'object') {
    throw new Error('Dosya içeriği bir JSON nesnesi değil.');
  }
  const obj = raw as Record<string, unknown>;
  if (obj.version === LEGACY_CONFIG_VERSION) {
    return migrateV1ToV2(obj as unknown as LegacyDashboardConfigV1);
  }
  if (obj.version !== CONFIG_VERSION) {
    throw new Error(
      `Desteklenmeyen sürüm: ${String(obj.version)}. Beklenen: ${CONFIG_VERSION}.`,
    );
  }
  // Minimal structural checks — full shape is trusted once `version` matches
  // the current schema. Stricter validation becomes a runtime migration
  // when the next config version ships.
  const required: Array<keyof DashboardConfig> = [
    'exportedAt',
    'sourceColumns',
    'modes',
    'columnOverrides',
    'valueGroupings',
    'compareConfig',
  ];
  for (const k of required) {
    if (!(k in obj)) {
      throw new Error(`Dosyada eksik alan: "${k}".`);
    }
  }
  return obj as unknown as DashboardConfig;
}

/** Build the per-column plan shown to the user in the review modal. */
export function planImport(
  config: DashboardConfig,
  currentColumns: readonly ColumnMeta[],
): ImportPlan {
  const referenced = collectReferencedColumns(config);
  const sourceByName = new Map<string, ColumnMeta>();
  for (const c of config.sourceColumns) sourceByName.set(c.name, c);
  const currentByName = new Map<string, ColumnMeta>();
  for (const c of currentColumns) currentByName.set(c.name, c);

  const autoResolved: ColumnResolution[] = [];
  const needsReview: ImportPlan['needsReview'] = [];

  for (const colName of referenced) {
    if (currentByName.has(colName)) {
      autoResolved.push({ kind: 'map', from: colName, to: colName });
      continue;
    }
    // Missing — surface candidates.
    const source: ColumnMeta = sourceByName.get(colName) ?? {
      name: colName,
      sqlType: 'VARCHAR',
    };
    needsReview.push({
      source,
      candidates: rankCandidates(source, currentColumns),
    });
  }

  return { version: CONFIG_VERSION, autoResolved, needsReview };
}

/**
 * Apply the user's (and auto-resolved) column decisions to the
 * config, producing the stores-shaped data the app can write through.
 */
export function applyImport(
  config: DashboardConfig,
  resolutions: readonly ColumnResolution[],
  targetDatasetId: string,
): AppliedImport {
  const remap = new Map<string, string>();
  const skipped = new Set<string>();
  for (const r of resolutions) {
    if (r.kind === 'map') remap.set(r.from, r.to);
    else skipped.add(r.from);
  }

  const dropped: AppliedImport['droppedWidgets'] = [];

  const applyMode = (
    slice: DashboardModeSlice,
    mode: DashboardMode,
  ): AppliedModeSlice => {
    const widgets: WidgetConfig[] = [];
    for (const w of slice.widgets) {
      const required = widgetRequiredColumns(w);
      const bad = required.find((c) => skipped.has(c));
      if (bad) {
        dropped.push({
          id: w.id,
          title: w.title,
          reason: `Atlanan kolon: "${bad}" (${mode})`,
        });
        continue;
      }
      widgets.push(remapWidget(w, remap, targetDatasetId, mode));
    }
    const pinnedFilters = slice.pinnedFilters
      .filter((c) => !skipped.has(c))
      .map((c) => remap.get(c) ?? c);
    const keptIds = new Set(widgets.map((w) => w.id));
    return {
      widgets,
      pinnedFilters,
      layouts: {
        kpi: slice.layouts.kpi.filter((item) => keptIds.has(item.i)),
        chart: slice.layouts.chart.filter((item) => keptIds.has(item.i)),
      },
    };
  };

  const columnOverrides: ColumnOverrides = {};
  for (const [col, override] of Object.entries(config.columnOverrides)) {
    if (skipped.has(col)) continue;
    const next = remap.get(col) ?? col;
    columnOverrides[next] = override;
  }

  const valueGroupings: Record<string, ColumnTransform> = {};
  for (const [col, transform] of Object.entries(config.valueGroupings)) {
    if (skipped.has(col)) continue;
    const next = remap.get(col) ?? col;
    valueGroupings[next] = remapTransformColumn(transform, remap);
  }

  // Remap the compare-config date column too (it might have been renamed
  // across exports). Drop the config when the user skipped that column.
  let compareConfig: AppliedImport['compareConfig'] = null;
  if (config.compareConfig) {
    const dateCol = config.compareConfig.dateColumn;
    if (!skipped.has(dateCol)) {
      compareConfig = {
        ...config.compareConfig,
        dateColumn: remap.get(dateCol) ?? dateCol,
      };
    }
  }

  return {
    modes: {
      normal: applyMode(config.modes.normal, 'normal'),
      compare: applyMode(config.modes.compare, 'compare'),
    },
    columnOverrides,
    valueGroupings,
    compareConfig,
    droppedWidgets: dropped,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function collectReferencedColumns(config: DashboardConfig): string[] {
  const seen = new Set<string>();
  for (const slice of [config.modes.normal, config.modes.compare]) {
    for (const w of slice.widgets) {
      for (const c of widgetRequiredColumns(w)) seen.add(c);
    }
    for (const c of slice.pinnedFilters) seen.add(c);
  }
  for (const c of Object.keys(config.columnOverrides)) seen.add(c);
  for (const c of Object.keys(config.valueGroupings)) seen.add(c);
  if (config.compareConfig) seen.add(config.compareConfig.dateColumn);
  return [...seen];
}

function widgetRequiredColumns(w: WidgetConfig): string[] {
  const out: string[] = [w.metric.column];
  if (w.groupBy) out.push(w.groupBy);
  if (w.extraMetrics) for (const m of w.extraMetrics) out.push(m.column);
  return out;
}

function remapWidget(
  w: WidgetConfig,
  remap: Map<string, string>,
  targetDatasetId: string,
  mode: DashboardMode,
): WidgetConfig {
  const col = (c: string): string => remap.get(c) ?? c;
  const next: WidgetConfig = {
    ...w,
    datasetId: targetDatasetId,
    mode,
    metric: {
      ...w.metric,
      column: col(w.metric.column),
    },
  };
  if (w.groupBy) next.groupBy = col(w.groupBy);
  if (w.extraMetrics) {
    next.extraMetrics = w.extraMetrics.map((m) => ({
      ...m,
      column: col(m.column),
    }));
  }
  return next;
}

function remapTransformColumn(
  t: ColumnTransform,
  remap: Map<string, string>,
): ColumnTransform {
  const next = remap.get(t.column) ?? t.column;
  if (t.kind === 'map') {
    return {
      kind: 'map',
      column: next,
      mapping: { ...t.mapping },
      fallback: t.fallback,
      ...(t.otherLabel !== undefined ? { otherLabel: t.otherLabel } : {}),
    };
  }
  return {
    kind: 'bin',
    column: next,
    bins: t.bins.map((b) => ({ ...b })),
    ...(t.otherLabel !== undefined ? { otherLabel: t.otherLabel } : {}),
  };
}

/**
 * Migrate a v1 (single-mode) bundle into the v2 shape: legacy slice
 * lands in `modes.normal`, compare slice is empty, no compare config.
 * Performed inline during parse so the rest of the pipeline only ever
 * deals with v2.
 */
function migrateV1ToV2(legacy: LegacyDashboardConfigV1): DashboardConfig {
  const emptyCompareSlice: DashboardModeSlice = {
    widgets: [],
    pinnedFilters: [],
    layouts: { kpi: [], chart: [] },
  };
  return {
    version: CONFIG_VERSION,
    exportedAt: legacy.exportedAt,
    sourceColumns: legacy.sourceColumns,
    modes: {
      normal: {
        widgets: legacy.widgets,
        pinnedFilters: legacy.pinnedFilters,
        layouts: legacy.layouts,
      },
      compare: emptyCompareSlice,
    },
    columnOverrides: legacy.columnOverrides,
    valueGroupings: legacy.valueGroupings,
    compareConfig: null,
  };
}
